import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, User, LogOut, Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { pushSupported, urlBase64ToUint8Array } from '../../utils/pushSubscription';

interface TopBarProps {
  onMenuClick: () => void;
}

type InAppNotificationRow = {
  id: string;
  type: string;
  title: string;
  body: string | null;
  maintenanceReportId: string | null;
  readAt: string | null;
  createdAt: string | null;
};

const POLL_MS = 45_000;

export const TopBar: React.FC<TopBarProps> = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<InAppNotificationRow[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [pushRegistering, setPushRegistering] = useState(false);
  const [pushHint, setPushHint] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  const maintenanceReportHref = useCallback((reportId: string) => `/r/mantenimiento/${reportId}`, []);

  const enableDevicePush = async () => {
    setPushHint(null);
    if (!pushSupported()) {
      setPushHint('Tu navegador no soporta avisos en el dispositivo.');
      return;
    }
    setPushRegistering(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        setPushHint('Sin permiso no podemos mostrar avisos. Revisa la configuración del sitio en el navegador.');
        return;
      }
      const { configured, publicKey } = await api.getPushVapidPublicKey();
      if (!configured || !publicKey) {
        setPushHint('Falta configurar VAPID en el servidor (variables de entorno).');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }
      const json = sub.toJSON();
      if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) {
        setPushHint('No se pudo leer la suscripción del navegador.');
        return;
      }
      await api.subscribePush({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setPushHint('Dispositivo registrado. En iPhone/iPad: instala la app desde Safari (Compartir → Añadir a inicio), iOS 16.4 o superior.');
    } catch (e) {
      console.error(e);
      setPushHint('No se pudo completar el registro. Intenta de nuevo.');
    } finally {
      setPushRegistering(false);
    }
  };

  const refreshUnread = useCallback(async () => {
    if (!user) return;
    try {
      const { count } = await api.getNotificationsUnreadCount();
      setUnreadCount(count);
    } catch {
      /* ignorar si red cae */
    }
  }, [user]);

  const loadNotificationsList = useCallback(async () => {
    if (!user) return;
    try {
      setNotificationsLoading(true);
      const rows = await api.getNotifications(40);
      setNotifications(rows);
    } catch {
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void refreshUnread();
    const t = window.setInterval(() => void refreshUnread(), POLL_MS);
    return () => window.clearInterval(t);
  }, [refreshUnread]);

  useEffect(() => {
    if (showNotifications) void loadNotificationsList();
  }, [showNotifications, loadNotificationsList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const t = event.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(t)) {
        setShowDropdown(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(t)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openNotification = async (n: InAppNotificationRow) => {
    if (!n.readAt) {
      try {
        await api.markNotificationRead(n.id);
        setNotifications((prev) =>
          prev.map((x) => (x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x)),
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        /* continuar */
      }
    }
    setShowNotifications(false);
    if (n.maintenanceReportId) {
      navigate(maintenanceReportHref(n.maintenanceReportId));
      return;
    }
    if (n.type === 'delivery_inspection_assigned') {
      navigate('/delivery-inspections');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications((prev) => prev.map((x) => ({ ...x, readAt: x.readAt ?? new Date().toISOString() })));
      setUnreadCount(0);
    } catch {
      /* noop */
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 py-3 lg:px-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            type="button"
            onClick={onMenuClick}
            className="lg:hidden text-gray-600 hover:text-gray-900 p-2"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>

        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="relative" ref={notificationsRef}>
            <button
              type="button"
              onClick={() => {
                setShowNotifications(!showNotifications);
                setShowDropdown(false);
              }}
              className="relative p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              aria-label={
                unreadCount > 0
                  ? `Notificaciones, ${unreadCount} sin leer`
                  : 'Notificaciones'
              }
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[1.125rem] h-[1.125rem] px-0.5 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white leading-none">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-[min(100vw-2rem,22rem)] max-h-[min(70vh,24rem)] overflow-hidden flex flex-col bg-white rounded-lg shadow-lg border border-gray-200 z-30">
                <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-gray-900">Notificaciones</span>
                  {unreadCount > 0 && (
                    <button
                      type="button"
                      onClick={() => void handleMarkAllRead()}
                      className="text-xs text-primary hover:underline font-medium"
                    >
                      Marcar todas leídas
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto flex-1">
                  {notificationsLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-primary" />
                    </div>
                  ) : notifications.length === 0 ? (
                    <p className="text-sm text-gray-500 px-3 py-6 text-center">No hay notificaciones.</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {notifications.map((n) => {
                        const unread = !n.readAt;
                        return (
                          <li key={n.id}>
                            <button
                              type="button"
                              onClick={() => void openNotification(n)}
                              className={`w-full px-3 py-2.5 text-left relative hover:bg-gray-50 transition-colors ${
                                unread ? 'bg-primary/5' : ''
                              } ${n.maintenanceReportId ? 'cursor-pointer' : ''}`}
                            >
                              <div className="text-sm font-medium text-gray-900 pr-6">{n.title}</div>
                              {n.body && (
                                <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.body}</div>
                              )}
                              {n.createdAt && (
                                <div className="text-[10px] text-gray-400 mt-1">
                                  {new Date(n.createdAt).toLocaleString('es-MX', {
                                    dateStyle: 'short',
                                    timeStyle: 'short',
                                  })}
                                </div>
                              )}
                              {unread && (
                                <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-primary shrink-0" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
                <div className="border-t border-gray-100 px-3 py-2 space-y-2 bg-gray-50 shrink-0">
                  <p className="text-[10px] text-gray-500 leading-snug">
                    Instala la app desde el navegador (menú «Añadir a pantalla de inicio») para usarla como PWA.
                  </p>
                  {pushSupported() &&
                    (typeof Notification === 'undefined' ||
                      Notification.permission === 'default' ||
                      Notification.permission === 'granted') && (
                      <button
                        type="button"
                        disabled={pushRegistering}
                        onClick={() => void enableDevicePush()}
                        className="w-full text-left text-xs font-medium text-primary hover:underline disabled:opacity-50"
                      >
                        {pushRegistering ? 'Activando avisos…' : 'Avisos en celular o iPad (push)'}
                      </button>
                    )}
                  {typeof Notification !== 'undefined' && Notification.permission === 'denied' && (
                    <p className="text-[10px] text-amber-800">
                      Las notificaciones están bloqueadas para este sitio; actívalas en ajustes del navegador.
                    </p>
                  )}
                  {pushHint && <p className="text-[10px] text-gray-700 leading-snug">{pushHint}</p>}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => {
                setShowDropdown(!showDropdown);
                setShowNotifications(false);
              }}
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center shrink-0">
                <User className="w-5 h-5 text-white" />
              </div>
              <span className="hidden md:block text-sm font-medium text-gray-700">
                {user?.name || 'Usuario'}
              </span>
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                <div className="py-2">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                    <p className="text-xs text-gray-500">{user?.email}</p>
                    <p className="text-xs text-gray-400 mt-1">{user?.role?.name || 'Rol'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      setShowDropdown(false);
                      await logout();
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Cerrar Sesión</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
